/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: PhonebookImportView.tsx
 🗓️ Created: 2025-12-05
 🧩 Description:
    CSV import view for external phonebook contacts
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Upload, FileText, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { ModuleFormHeader } from '../../components/layouts/ModuleFormHeader';
import { FormBox } from '../../components/ui/FormBox';
import { Button } from '../../components/ui/button';
import { importContacts } from './api/phonebooksApi';
import type { CSVContact, ImportResult } from './types';

export default function PhonebookImportView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();

  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setImportResult(null);
    } else {
      showError(t('phonebooks.messages.invalidFileType'));
    }
  }, [showError, t]);

  const parseCSV = useCallback(async (csvText: string): Promise<CSVContact[]> => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error(t('phonebooks.messages.emptyFile'));
    }

    const headers = lines[0].split(',').map(h => h.trim());
    const contacts: CSVContact[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const contact: Record<string, string> = {};

      headers.forEach((header, index) => {
        contact[header] = values[index] || '';
      });

      // Validate required fields
      if (contact['first_name'] && contact['phone']) {
        contacts.push(contact as unknown as CSVContact);
      }
    }

    return contacts;
  }, [t]);

  const handleImport = useCallback(async () => {
    if (!file || !id) return;

    try {
      setImporting(true);
      
      // Read file
      const text = await file.text();
      const contacts = await parseCSV(text);

      if (contacts.length === 0) {
        showError(t('phonebooks.messages.noValidContacts'));
        return;
      }

      // Import contacts
      const result = await importContacts(id, contacts);
      setImportResult(result);

      if (result.imported > 0) {
        showSuccess(
          t('phonebooks.messages.importSuccess', {
            count: result.imported,
          })
        );
      }

      if (result.errors.length > 0) {
        console.error('Import errors:', result.errors);
      }
    } catch (error) {
      showError(error instanceof Error ? error.message : t('phonebooks.messages.importError'));
    } finally {
      setImporting(false);
    }
  }, [file, id, parseCSV, showSuccess, showError, t]);

  return (
    <div className="p-6">
      <ModuleFormHeader
        icon={<Upload className="h-5 w-5" />}
        title={t('phonebooks.importTitle')}
        backButtonText={t('phonebooks.actions.backToPhonebook')}
        onBack={() => navigate(`/pbx/applications/phonebooks/${id}`)}
      />

      <div className="mt-8 space-y-6 max-w-3xl">
        {/* Instructions */}
        <FormBox title={t('phonebooks.import.instructions')} icon={<FileText className="h-4 w-4" />}>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('phonebooks.import.description')}
            </p>
            
            <div className="bg-muted/50 p-4 rounded-md">
              <p className="text-xs font-mono text-foreground mb-2">
                {t('phonebooks.import.format')}
              </p>
              <code className="text-xs text-muted-foreground block">
                first_name,last_name,phone,mobile_number,home_number,organization,job_title,email
              </code>
              <code className="text-xs text-muted-foreground block mt-1">
                John,Doe,+1234567890,+0987654321,,Acme Inc,Manager,john@example.com
              </code>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">
                {t('phonebooks.import.requiredFields')}:
              </p>
              <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                <li>first_name</li>
                <li>phone</li>
              </ul>
            </div>
          </div>
        </FormBox>

        {/* File Upload */}
        <FormBox title={t('phonebooks.import.upload')} icon={<Upload className="h-4 w-4" />}>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="block w-full text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
              />
            </div>

            {file && (
              <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm text-blue-800 dark:text-blue-300">{file.name}</span>
                </div>
                <Button
                  onClick={handleImport}
                  disabled={importing}
                  className="inline-flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  {importing ? t('phonebooks.actions.importing') : t('phonebooks.actions.importFile')}
                </Button>
              </div>
            )}
          </div>
        </FormBox>

        {/* Import Results */}
        {importResult && (
          <FormBox title={t('phonebooks.import.results')} icon={<CheckCircle className="h-4 w-4" />}>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">
                  {t('phonebooks.import.imported')}: {importResult.imported}
                </span>
              </div>

              {importResult.skipped > 0 && (
                <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400">
                  <AlertCircle className="h-5 w-5" />
                  <span className="font-medium">
                    {t('phonebooks.import.skipped')}: {importResult.skipped}
                  </span>
                </div>
              )}

              {importResult.errors.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                    <XCircle className="h-5 w-5" />
                    <span className="font-medium">
                      {t('phonebooks.import.errors')}: {importResult.errors.length}
                    </span>
                  </div>
                  <div className="max-h-40 overflow-y-auto bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                    {importResult.errors.map((error, index) => (
                      <p key={index} className="text-xs text-red-800 dark:text-red-300">
                        {error}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-4">
                <Button onClick={() => navigate(`/pbx/applications/phonebooks/${id}/entries`)}>
                  {t('phonebooks.actions.viewEntries')}
                </Button>
              </div>
            </div>
          </FormBox>
        )}
      </div>
    </div>
  );
}
